import { getUserFacingErrorMessage } from '@whistler-ui/k2/helpers/userFacingError';

import {
  deleteNetworkDetails,
  getNetworkDetails,
  postNetworkDetails,
  putNetworkDetails,
} from '../../services/accounts';
import types from './types';

// rcs_verified is read-only on POST/PUT /network_details (UES-12200); it is
// only set through PUT /network_details/{id}/rcs-verified.
const READ_ONLY_FIELDS = ['rcs_verified'];

const stripReadOnlyFields = row => {
  return Object.fromEntries(
    Object.entries(row).filter(([key]) => !READ_ONLY_FIELDS.includes(key)),
  );
};

const actions = dispatch => {
  return {
    persistNetworkDetails: async ({ data, organisationId } = {}) => {
      const newRows = data.filter(({ id }) => id.includes('new-'));
      const updatedRows = data.filter(({ id }) => id.includes('edited-'));
      const deletedRows = data.filter(({ id }) => id.includes('deleted-'));

      const deletePromises = deletedRows.map(({ id: tempId }) => {
        const id = tempId.replace('deleted-', '');
        return { [tempId]: deleteNetworkDetails(id) };
      });

      const updatePromises = updatedRows.map(({ id: tempId, ...rest }) => {
        const id = tempId.replace('edited-', '');
        return {
          [tempId]: putNetworkDetails({
            ...stripReadOnlyFields(rest),
            id,
            organisation_id: organisationId,
          }),
        };
      });

      const createPromises = newRows.map(({ id, ...rest }) => {
        return {
          [id]: postNetworkDetails({
            ...stripReadOnlyFields(rest),
            organisation_id: organisationId,
          }),
        };
      });

      const promises = [
        ...deletePromises,
        ...updatePromises,
        ...createPromises,
      ];

      const responses = { error: {}, success: [], deleted: [] };

      for (const promise of promises) {
        const [id] = Object.keys(promise);
        try {
          const response = await promise[id];
          if (id.includes('deleted-')) {
            responses.deleted.push({ tempId: id });
          } else {
            responses.success.push({ ...response, tempId: id });
          }
        } catch (e) {
          responses.error[id] = getUserFacingErrorMessage(
            e,
            'Something went wrong',
          )
            .replaceAll(/[-+.^:{}&,;[]/g, '')
            .slice(0, 255);
        }
      }

      dispatch({
        type: types.UPDATE_NETWORK_DETAILS,
        payload: responses,
      });

      return responses;
    },
    getNetworkDetails: async ({ silent = true } = {}) => {
      try {
        const networkDetailsResponse = await getNetworkDetails();
        dispatch({
          type: types.SET_NETWORK_DETAILS,
          payload: networkDetailsResponse,
        });
      } catch (error) {
        if (silent) return null;
        throw error;
      }
    },
    setNetworkDetails: ({ networkDetails }) => {
      dispatch({
        type: types.SET_NETWORK_DETAILS,
        payload: networkDetails,
      });
    },
    // Server-side change already applied (e.g. rcs_verified via its own
    // endpoint): merge it into the matching row without touching pending
    // new-/edited-/deleted- state or the unsaved-changes flag.
    patchNetworkDetailsRow: ({ persistedId, changes }) => {
      dispatch({
        type: types.PATCH_NETWORK_DETAILS_ROW,
        payload: { persistedId, changes },
      });
    },
    setUnsavedChanges: hasUnsavedChanges => {
      dispatch({
        type: types.SET_NETWORK_DETAILS_UNSAVED_CHANGES,
        payload: hasUnsavedChanges,
      });
    },
  };
};

export default actions;
